<template>
	<div class="pt">
		<p v-if="loading && !profile" class="pt-note">Loading…</p>
		<template v-else-if="profile">
			<p class="pt-summary">
				Built from <strong>{{ profile.totalEvents || 0 }} actions</strong> on the site.
				<template v-if="careerDirection.primaryDomain">You lean most towards <strong>{{ nameOf(careerDirection.primaryDomain) }}</strong>.</template>
			</p>

			<!-- Career direction -->
			<section class="pt-card" :style="{ '--area': colors.career }">
				<header class="pt-card__head">
					<h3>Career direction</h3>
				</header>
				<p class="pt-card__what">The kind of work you lean towards.</p>
				<p v-if="!career.length" class="pt-empty">Nothing yet. Open a few jobs, courses or assessments.</p>

				<div v-for="domain in career" :key="domain.tag" class="pt-domain" :class="{ 'pt-domain--muted': !domain.score }">
					<div class="pt-line">
						<span class="pt-name">{{ domain.name || tagName(domain.tag) }}</span>
						<span class="pt-level">{{ strength(domain.score) }}</span>
						<span class="pt-value">{{ whole(domain.score) }}</span>
					</div>
					<div class="pt-bar"><i :style="{ width: pct(domain.score) }" /></div>
					<p class="pt-caption">
						{{ evidenceText(domain) }}
						<b v-if="change(domain.tag, domain.score) >= 1" class="pt-up">▲ {{ whole(change(domain.tag, domain.score)) }}</b>
					</p>

					<ul v-if="domain.subdomains.length" class="pt-subs">
						<li v-for="sub in domain.subdomains" :key="sub.tag" :class="{ 'pt-domain--muted': !sub.score }">
							<span class="pt-name pt-name--sub">{{ sub.name || tagName(sub.tag) }}</span>
							<span class="pt-minibar"><i :style="{ width: pct(sub.score) }" /></span>
							<span class="pt-value pt-value--sub">{{ sub.score ? whole(sub.score) : "–" }}</span>
						</li>
					</ul>
				</div>
			</section>

			<!-- Recent focus -->
			<section class="pt-card" :style="{ '--area': colors.recent }">
				<h3>Right now</h3>
				<p class="pt-card__what">Your focus over the last few days.</p>
				<p v-if="!recentRows.length" class="pt-empty">Nothing recent yet.</p>
				<div v-for="row in recentRows" :key="row.tag" class="pt-row">
					<span class="pt-name pt-name--sub">{{ nameOf(row.tag) }}</span>
					<span class="pt-minibar"><i :style="{ width: row.value + '%' }" /></span>
					<span class="pt-value pt-value--sub">{{ whole(row.value) }}%</span>
				</div>
			</section>

			<!-- Learning momentum -->
			<section class="pt-card" :style="{ '--area': colors.momentum }">
				<h3>Learning momentum</h3>
				<p class="pt-card__what">How actively you are learning right now.</p>
				<div class="pt-big">
					<strong>{{ whole(momentum.overall.value) }}</strong>
					<span v-if="momentum.overall.value > 0">
						In {{ momentumHorizon }} with no learning: <b>{{ whole(momentumIn5Days) }}</b>
					</span>
					<span v-else>No learning activity yet</span>
				</div>

				<template v-if="builtFrom.length">
					<h4>Built from</h4>
					<div v-for="item in builtFrom" :key="item.event" class="pt-row">
						<span class="pt-name pt-name--sub">{{ item.count }} {{ item.label }}</span>
						<span class="pt-plus">+{{ whole(item.weight) }} each</span>
					</div>
				</template>
				<p v-if="notYet.length" class="pt-hint2">
					Also raises it: {{ notYet.map((item) => `${item.label} (+${whole(item.weight)})`).join(", ") }}
				</p>

				<template v-if="momentum.byDomain.length">
					<h4>By area</h4>
					<div v-for="row in momentum.byDomain" :key="row.tag" class="pt-row">
						<span class="pt-name pt-name--sub">{{ row.name || tagName(row.tag) }}</span>
						<span class="pt-minibar"><i :style="{ width: momentumPct(row.value) }" /></span>
						<span class="pt-value pt-value--sub">{{ whole(row.value) }}</span>
					</div>
				</template>
			</section>

			<!-- Assessment confidence -->
			<section class="pt-card" :style="{ '--area': colors.assessment }">
				<h3>Assessment confidence</h3>
				<p class="pt-card__what">How you score in assessments.</p>
				<p v-if="!assessment.available" class="pt-empty">Not measured yet. It appears once assessment results are scored.</p>
				<template v-else>
					<div v-for="(value, tag) in assessment.byDomain" :key="tag" class="pt-row">
						<span class="pt-name pt-name--sub">{{ nameOf(tag) }}</span>
						<span class="pt-value pt-value--sub">{{ whole(value) }}</span>
					</div>
				</template>
			</section>

			<!-- Dashboard arrangement -->
			<section class="pt-card" :style="{ '--area': colors.sections }">
				<header class="pt-card__head">
					<h3>Dashboard order</h3>
					<span v-if="countdown" class="pt-timer">Changes in {{ countdown }}</span>
				</header>
				<p class="pt-card__what">The order is held for {{ holdText }} so it doesn't change on every refresh.</p>

				<div class="pt-arrange">
					<div>
						<h4>Showing now</h4>
						<ol v-if="servedOrder.length" class="pt-order">
							<li v-for="name in servedOrder" :key="'s' + name"><span class="pt-name pt-name--sub">{{ sectionLabel(name) }}</span></li>
						</ol>
						<p v-else class="pt-hint2">Picked the next time the dashboard loads.</p>
					</div>
					<div>
						<h4>{{ servedOrder.length ? "Next" : "Will show" }}</h4>
						<ol v-if="upcomingOrder.length" class="pt-order" :class="{ 'pt-order--same': sameOrder }">
							<li v-for="name in upcomingOrder" :key="'u' + name"><span class="pt-name pt-name--sub">{{ sectionLabel(name) }}</span></li>
						</ol>
						<p v-if="servedOrder.length && upcomingOrder.length" class="pt-hint2">{{ sameOrder ? "Same as now." : "Different from now." }}</p>
					</div>
				</div>

				<h4>Based on your interest</h4>
				<div v-for="section in orderedSections" :key="section.name" class="pt-row">
					<span class="pt-name pt-name--sub">{{ tagName(section.name) }}</span>
					<span class="pt-minibar"><i :style="{ width: sectionPct(section.value) }" /></span>
					<span class="pt-value pt-value--sub">{{ whole(section.value) }}</span>
					<span class="pt-share">{{ sectionShare(section.value) }}%</span>
				</div>
			</section>

			<!-- Activity -->
			<section class="pt-card" :style="{ '--area': colors.activity }">
				<h3>Activity</h3>
				<p v-if="lastActive" class="pt-card__what">Last active {{ lastActive }}.</p>
				<p v-if="!activity.length" class="pt-empty">No activity counted yet.</p>
				<div v-else class="pt-chips">
					<span v-for="item in activity" :key="item.key" class="pt-chip"><b>{{ item.value }}</b> {{ item.label }}</span>
				</div>
			</section>
		</template>
	</div>
</template>

<script>
import { AREA_COLORS, cardName, durationLabel, strength, tagName, whole } from "./hudFormat";

export default {
	name: "HudProfileTab",
	props: {
		insights: { type: Object, default: null },
		baseline: { type: Object, default: null },
		loading: { type: Boolean, default: false },
	},
	data() {
		return { colors: AREA_COLORS, now: Date.now(), ticker: null };
	},
	mounted() {
		// Drives the "Changes in" countdown between the 15 s refreshes.
		this.ticker = setInterval(() => {
			this.now = Date.now();
		}, 1000);
	},
	beforeDestroy() {
		if (this.ticker) clearInterval(this.ticker);
	},
	computed: {
		profile() {
			return this.insights && this.insights.profile;
		},
		signals() {
			return (this.insights && this.insights.signals) || {};
		},
		careerDirection() {
			return (this.profile && this.profile.careerDirection) || {};
		},
		career() {
			return this.signals.career || [];
		},
		names() {
			const map = {};
			this.career.forEach((domain) => {
				map[domain.tag] = domain.name;
				domain.subdomains.forEach((sub) => {
					map[sub.tag] = sub.name;
				});
			});
			return map;
		},
		recentRows() {
			const shares = (this.careerDirection.recentIntent || {}).domains || {};
			return Object.keys(shares)
				.map((tag) => ({ tag, value: Number(shares[tag]) || 0 }))
				.sort((a, b) => b.value - a.value);
		},
		momentum() {
			return this.signals.learningMomentum || { overall: { value: 0, count: 0 }, byDomain: [] };
		},
		arrangement() {
			return (this.insights && this.insights.dashboard) || {};
		},
		servedOrder() {
			return (this.arrangement.served && this.arrangement.served.sectionOrder) || [];
		},
		upcomingOrder() {
			return (this.arrangement.upcoming && this.arrangement.upcoming.sectionOrder) || [];
		},
		sameOrder() {
			return this.servedOrder.join() === this.upcomingOrder.join();
		},
		countdown() {
			const left = this.arrangement.expiresInSeconds;
			const readAt = Date.parse(this.insights && this.insights.generatedAt);
			if (!(left >= 0) || !isFinite(readAt)) return "";
			const remaining = Math.max(0, Math.round(left - (this.now - readAt) / 1000));
			if (!remaining) return "moments";
			return `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;
		},
		holdText() {
			const seconds = this.arrangement.holdSeconds;
			if (!(seconds > 0)) return "no time";
			return seconds % 60 === 0 ? `${seconds / 60} minute${seconds === 60 ? "" : "s"}` : `${seconds} seconds`;
		},
		momentumIn5Days() {
			const projected = this.momentum.overall.projected || [];
			return projected.length ? projected[projected.length - 1].value : 0;
		},
		// The last look-ahead: 5 days normally, minutes in decay test mode.
		momentumHorizon() {
			const projected = this.momentum.overall.projected || [];
			const last = projected[projected.length - 1];
			if (!last) return "5 days";
			return durationLabel(last.seconds != null ? last.seconds : last.hours * 3600);
		},
		builtFrom() {
			return (this.momentum.sources || []).filter((item) => item.count > 0).map(labelled);
		},
		notYet() {
			return (this.momentum.sources || []).filter((item) => !item.count).map(labelled);
		},
		assessment() {
			return (this.profile && this.profile.assessmentConfidence) || { available: false, byDomain: {} };
		},
		orderedSections() {
			const byName = {};
			(this.signals.sections || []).forEach((s) => {
				byName[s.name] = s;
			});
			const order = (this.profile && this.profile.sectionOrder) || Object.keys(byName);
			return order.map((name) => byName[name] || { name, value: 0 });
		},
		activity() {
			const items = [];
			const funnel = (this.profile && this.profile.funnel) || {};
			Object.keys(funnel).forEach((key) => items.push({ key, value: funnel[key], label: humanise(key) }));
			const cards = (this.profile && this.profile.topCardClicks) || {};
			Object.keys(cards).forEach((key) => items.push({ key: `card-${key}`, value: cards[key], label: `clicks on ${cardName(key)}` }));
			return items.filter((item) => Number(item.value) > 0).sort((a, b) => b.value - a.value);
		},
		lastActive() {
			const at = ((this.profile && this.profile.engagement) || {}).lastActiveAt;
			const ms = Date.parse(at);
			if (!isFinite(ms)) return "";
			const minutes = Math.round((Date.now() - ms) / 60000);
			if (minutes < 1) return "just now";
			if (minutes < 60) return `${minutes} min ago`;
			const hours = Math.round(minutes / 60);
			return hours < 24 ? `${hours} h ago` : new Date(ms).toLocaleDateString();
		},
	},
	methods: {
		strength,
		tagName,
		whole,
		sectionLabel(name) {
			return SECTION_LABELS[name] || tagName(String(name).replace(/_/g, "-"));
		},
		nameOf(tag) {
			return this.names[tag] || tagName(tag);
		},
		pct(value) {
			return `${Math.min(100, Math.max(0, Number(value) || 0))}%`;
		},
		sectionShare(value) {
			const total = (this.signals.sections || []).reduce((sum, s) => sum + (s.value > 0 ? s.value : 0), 0);
			return total > 0 ? Math.round((value / total) * 100) : 0;
		},
		momentumPct(value) {
			const top = Math.max(0, ...this.momentum.byDomain.map((row) => row.value));
			return top > 0 ? `${Math.max(4, (value / top) * 100)}%` : "0%";
		},
		sectionPct(value) {
			const top = Math.max(0, ...(this.signals.sections || []).map((s) => s.value));
			return top > 0 ? `${Math.max(4, (value / top) * 100)}%` : "0%";
		},
		change(key, value) {
			if (!this.baseline) return 0;
			return (Number(value) || 0) - (Number(this.baseline[key]) || 0);
		},
		evidenceText(node) {
			const parts = [];
			if (node.views.count) parts.push(`${node.views.count} view${node.views.count === 1 ? "" : "s"}`);
			if (node.career.count) parts.push(`${node.career.count} action${node.career.count === 1 ? "" : "s"}`);
			return parts.join(" · ");
		},
	},
};

const SECTION_LABELS = {
	jobs: "Jobs",
	courses: "Courses",
	assessments: "Assessments",
	ai_mock_interview: "AI mock interview",
};

const MOMENTUM_LABELS = {
	"course.viewed": "course views",
	"course.enroll_intent": "enrol clicks",
	"course.purchased": "courses bought",
	"assessment.viewed": "assessment views",
	"assessment.purchased": "assessments bought",
	"job.applied": "job applications",
};

function labelled(item) {
	return { ...item, label: MOMENTUM_LABELS[item.event] || item.event };
}

function humanise(key) {
	return String(key)
		.replace(/([A-Z])/g, " $1")
		.toLowerCase()
		.trim();
}
</script>

<style scoped>
.pt {
	padding: 12px 14px 24px;
}
.pt-note,
.pt-empty {
	margin: 4px 0 0;
	font-size: 13px;
	color: #72738e;
}
.pt-summary {
	margin: 0 0 12px;
	font-size: 13px;
	line-height: 1.5;
	color: #464667;
}
.pt-summary strong {
	color: #1a4164;
}

.pt-card {
	position: relative;
	padding: 12px 14px 12px 16px;
	margin-bottom: 10px;
	border: 1px solid #e2e8f0;
	border-left: 4px solid var(--area);
	border-radius: 10px;
	background: #fff;
}
.pt-card h3 {
	margin: 0;
	font: 700 14px/1.3 "Montserrat", sans-serif;
	color: var(--area);
}
.pt-card__head {
	display: flex;
	justify-content: space-between;
	align-items: baseline;
}
.pt-card__what {
	margin: 2px 0 10px;
	font-size: 12px;
	line-height: 1.45;
	color: #72738e;
}
.pt-domain {
	padding: 12px 0;
	border-top: 1px solid #eef2f6;
}
.pt-domain:first-of-type {
	border-top: none;
	padding-top: 0;
}
.pt-domain--muted {
	opacity: 0.6;
}
.pt-line {
	display: flex;
	align-items: baseline;
	gap: 8px;
}
.pt-name {
	flex: 1;
	min-width: 0;
	font: 700 14px/1.35 "Montserrat", sans-serif;
	color: #1a4164;
}
.pt-name--sub {
	font: 400 13px/1.3 "Lato", sans-serif;
	color: #333333;
}
.pt-level {
	padding: 1px 8px;
	border-radius: 10px;
	background: color-mix(in srgb, var(--area) 12%, #fff);
	color: var(--area);
	font: 600 11px/1.6 "Montserrat", sans-serif;
}
.pt-value {
	min-width: 28px;
	text-align: right;
	font: 700 18px "Montserrat", sans-serif;
	color: var(--area);
	font-variant-numeric: tabular-nums;
}
.pt-value--sub {
	font-size: 13px;
}
.pt-bar {
	height: 8px;
	margin: 6px 0 4px;
	border-radius: 4px;
	background: #eef2f6;
	overflow: hidden;
}
.pt-bar i,
.pt-minibar i {
	display: block;
	height: 100%;
	border-radius: inherit;
	background: var(--area);
	transition: width 0.4s ease;
}
.pt-caption {
	margin: 0;
	font-size: 11.5px;
	color: #72738e;
}
.pt-up {
	margin-left: 6px;
	color: #038e00;
	font-weight: 700;
}

.pt-subs,
.pt-order {
	margin: 8px 0 0;
	padding: 0;
	list-style: none;
}
.pt-subs li,
.pt-order li,
.pt-row {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 4px 0;
}
.pt-subs {
	padding-left: 12px;
	border-left: 2px solid color-mix(in srgb, var(--area) 25%, #fff);
}
.pt-order {
	counter-reset: order;
}
.pt-order li::before {
	counter-increment: order;
	content: counter(order);
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: var(--area);
	color: #fff;
	font: 700 10.5px/18px "Montserrat", sans-serif;
	text-align: center;
}
.pt-minibar {
	flex: 0 0 38%;
	height: 6px;
	border-radius: 3px;
	background: #eef2f6;
	overflow: hidden;
}

.pt-big {
	display: flex;
	align-items: baseline;
	gap: 10px;
	margin-bottom: 4px;
}
.pt-big strong {
	font: 700 26px "Montserrat", sans-serif;
	color: var(--area);
}
.pt-big span {
	font-size: 12px;
	color: #72738e;
}

.pt-card h4 {
	margin: 10px 0 2px;
	font: 700 11.5px "Montserrat", sans-serif;
	color: #464667;
}
.pt-plus {
	font: 600 11.5px "Montserrat", sans-serif;
	color: var(--area);
}
.pt-hint2 {
	margin: 6px 0 0;
	font-size: 11.5px;
	color: #72738e;
}
.pt-big b {
	color: #1a4164;
}
.pt-share {
	min-width: 34px;
	text-align: right;
	font-size: 11.5px;
	color: #72738e;
	font-variant-numeric: tabular-nums;
}
.pt-timer {
	padding: 2px 8px;
	border-radius: 10px;
	background: color-mix(in srgb, var(--area) 12%, #fff);
	color: var(--area);
	font: 700 11px/1.6 "Montserrat", sans-serif;
	font-variant-numeric: tabular-nums;
}
.pt-arrange {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
	margin-bottom: 4px;
}
.pt-arrange h4 {
	margin-top: 0;
}
.pt-order--same {
	opacity: 0.55;
}
.pt-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}
.pt-chip {
	padding: 4px 10px;
	border-radius: 14px;
	background: #f6f9fb;
	border: 1px solid #e2e8f0;
	font-size: 12px;
	color: #464667;
}
.pt-chip b {
	color: #1a4164;
}
</style>
